import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { PUBLIC_TRACKED_PAGES } from '@/utils/publicPages';
import '@/styles/motion.css';

/**
 * SiteMotion — progressive motion layer for the public marketing pages.
 *
 * Nothing here changes markup by hand: on every public route it scans the
 * rendered page and tags elements with CSS hooks (see styles/motion.css):
 *   • <section>            → scroll reveal (fade + rise) via IntersectionObserver
 *   • card-like blocks     → staggered entrance + hover lift / photo zoom
 *   • gold / dark CTAs     → lift + sheen on hover
 *   • hero photo           → slow Ken Burns zoom
 *   • decorative swirls    → gentle drift
 *   • progress bars        → fill from 0 when their section appears
 *   • big numbers (94%…)   → count up when their section appears
 *   • eyebrow dots         → soft gold pulse
 *
 * All effects are opacity/transform only and are disabled by
 * `prefers-reduced-motion`. App/dashboard routes are left untouched.
 */

const CARD_RE = /(^|\s)rounded-(xl|2xl|3xl|\[(1[2-9]|[23]\d)px\])(\s|$)/;
const COUNT_RE = /^(₹)?(\d[\d,]*)(%|\+)?$/;

const isCardLike = (el) => {
  const cls = typeof el.className === 'string' ? el.className : '';
  if (!CARD_RE.test(cls)) return false;
  if (el.tagName === 'A' || el.tagName === 'BUTTON' || el.tagName === 'IMG' || el.tagName === 'INPUT') return false;
  if (el.offsetHeight < 140 || el.offsetWidth < 160) return false;
  const cs = getComputedStyle(el);
  if (cs.position === 'absolute' || cs.position === 'fixed' || cs.position === 'sticky') return false;
  return true;
};

const inlineBg = (el) => (el.style && el.style.backgroundColor) || '';
const isGold = (el) => /rgb\(241,\s*178,\s*0\)|#f1b200/i.test(inlineBg(el)) || /(^|\s)btn-primary(\s|$)/.test(el.className || '');

const easeOut = (t) => 1 - Math.pow(1 - t, 3);

function runCounter(el) {
  if (el.dataset.ssCounted) return;
  el.dataset.ssCounted = '1';
  const raw = el.textContent.trim();
  const m = raw.match(COUNT_RE);
  if (!m) return;
  const [, prefix = '', num, suffix = ''] = m;
  const target = parseInt(num.replace(/,/g, ''), 10);
  if (!Number.isFinite(target) || target === 0) return;
  const useCommas = num.includes(',');
  const fmt = (n) => (useCommas ? n.toLocaleString('en-IN') : String(n));
  const start = performance.now();
  const dur = 1200;
  const tick = (now) => {
    const p = Math.min(1, (now - start) / dur);
    el.textContent = `${prefix}${fmt(Math.round(target * easeOut(p)))}${suffix}`;
    if (p < 1) requestAnimationFrame(tick);
    else el.textContent = raw;
  };
  requestAnimationFrame(tick);
}

function tagSection(sec, io, gen) {
  // The marker is scoped to the current observer generation, not to the element
  // for all time. A route change tears the observer down; without this, a
  // section that React re-renders on a revisit would keep its stale "done"
  // flag, get .ss-reveal (opacity: 0) again, and never be observed by the new
  // observer — leaving it permanently invisible.
  if (sec.dataset.ssDone === gen) return;
  sec.dataset.ssDone = gen;
  if (sec.closest('nav, header, footer, [role="dialog"]')) return;
  // App-level live regions (sonner's toaster renders a <section aria-live>) and
  // empty/hidden sections must never be faded out.
  if (sec.hasAttribute('aria-live') || sec.closest('[data-sonner-toaster]')) return;
  if (sec.offsetHeight < 40 || (sec.parentElement && sec.parentElement.classList.contains('App'))) return;

  // Stagger children: outermost card-like blocks, in document order.
  let i = 0;
  sec.querySelectorAll('div, article, li').forEach((el) => {
    if (el.closest('.ss-child')) return;
    if (!isCardLike(el)) return;
    el.classList.add('ss-child', 'ss-card');
    el.style.setProperty('--ss-i', String(Math.min(i, 7)));
    i += 1;
  });

  // Progress bars: fill from 0.
  sec.querySelectorAll('div[style*="width"]').forEach((fill) => {
    const track = fill.parentElement;
    const w = fill.style.width;
    if (!track || !w || !w.endsWith('%')) return;
    const tcls = typeof track.className === 'string' ? track.className : '';
    if (!/rounded-full/.test(tcls) || !/overflow-hidden/.test(tcls)) return;
    fill.style.setProperty('--ss-w', w);
    fill.classList.add('ss-bar');
  });

  // Counters: big standalone numbers.
  const counters = [];
  sec.querySelectorAll('p, span, div, h2, h3').forEach((el) => {
    if (el.children.length !== 0) return;
    if (!COUNT_RE.test(el.textContent.trim())) return;
    if (parseFloat(getComputedStyle(el).fontSize) < 24) return;
    counters.push(el);
  });
  if (counters.length) sec.__ssCounters = counters;

  // Eyebrow dots.
  sec.querySelectorAll('span.w-2.h-2.rounded-full').forEach((d) => d.classList.add('ss-dot'));

  // Reveal: sections already on screen animate in on the next frame,
  // everything below the fold waits for the observer.
  const rect = sec.getBoundingClientRect();
  sec.classList.add('ss-reveal');
  if (rect.top < window.innerHeight * 0.9) {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      sec.classList.add('is-in');
      (sec.__ssCounters || []).forEach(runCounter);
    }));
  } else {
    io.observe(sec);
  }
}

/**
 * Last-resort guarantee that nothing stays invisible.
 *
 * The reveal only ever hides content that the observer is expected to bring
 * back. If anything breaks that contract — a torn-down observer, a section
 * scrolled horizontally inside a carousel, a browser that skips the callback
 * — the content must still be readable. Reveal every tagged section that is
 * within (or above) the viewport, regardless of the observer.
 */
function revealVisible(root) {
  root.querySelectorAll('.ss-reveal:not(.is-in)').forEach((sec) => {
    const r = sec.getBoundingClientRect();
    if (r.top < window.innerHeight && r.bottom > 0) {
      sec.classList.add('is-in');
      (sec.__ssCounters || []).forEach(runCounter);
    }
  });
}

function tagPage(root, io, gen) {
  const main = root.querySelector('main') || root;

  root.querySelectorAll('section').forEach((sec) => tagSection(sec, io, gen));

  // Hero photo: first section with a large cover image inside an overflow-hidden box.
  const hero = root.querySelector('section');
  if (hero && hero.dataset.ssHero !== gen) {
    hero.dataset.ssHero = gen;
    const img = hero.querySelector('img.object-cover');
    if (img && img.offsetHeight >= 380 && /hidden/.test(getComputedStyle(hero).overflow)) {
      img.classList.add('ss-kenburns');
    }
    // Stagger the hero copy: badge → heading → text → buttons.
    const h1 = hero.querySelector('h1');
    if (h1) {
      let box = h1.parentElement;
      while (box && box !== hero && box.children.length < 3) box = box.parentElement;
      if (box && box !== hero) {
        [...box.children].slice(0, 6).forEach((el, i) => {
          if (el.classList.contains('ss-child')) return;
          el.classList.add('ss-child');
          el.style.setProperty('--ss-i', String(i));
        });
      }
    }
  }

  // Decorative swirls.
  root.querySelectorAll('img[src*="swirl"]').forEach((el) => el.classList.add('ss-drift'));

  // Buttons / CTAs (rounded-xl links and buttons with an explicit background or border).
  main.querySelectorAll('a, button').forEach((el) => {
    if (el.dataset.ssBtn) return;
    const cls = typeof el.className === 'string' ? el.className : '';
    if (!/rounded-(xl|\[12px\])/.test(cls) && !/btn-primary/.test(cls)) return;
    if (el.closest('nav, header, footer')) return;
    el.dataset.ssBtn = '1';
    el.classList.add(isGold(el) ? 'ss-cta' : 'ss-btn');
  });

  // Footer links.
  root.querySelectorAll('footer a').forEach((a) => a.classList.add('ss-link'));

  // Lazy images fade in once decoded.
  root.querySelectorAll('img[loading="lazy"]:not(.ss-img):not(.ss-kenburns)').forEach((img) => {
    img.classList.add('ss-img');
    const done = () => img.classList.add('is-loaded');
    if (img.complete && img.naturalWidth > 0) done();
    else { img.addEventListener('load', done, { once: true }); img.addEventListener('error', done, { once: true }); }
  });

  // Horizontal carousels: hide their scrollbar.
  root.querySelectorAll('.snap-x').forEach((el) => el.classList.add('ss-scroll-hide'));

  // Skip-link target: the first main landmark, or the first section after the header.
  if (!root.getElementById('main')) {
    const target = root.querySelector('main') || root.querySelector('section');
    if (target) target.id = 'main';
  }
}

export default function SiteMotion() {
  const { pathname } = useLocation();

  useEffect(() => {
    const p = pathname.replace(/\/+$/, '') || '/';
    const isPublic = PUBLIC_TRACKED_PAGES.has(p) || p.startsWith('/blogs');
    if (!isPublic) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    document.body.classList.add('ss-page-enter');
    const enterTimer = setTimeout(() => document.body.classList.remove('ss-page-enter'), 700);

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        (e.target.__ssCounters || []).forEach(runCounter);
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });

    // Identifies this observer instance. Tagging markers carry it so a revisit
    // (which builds a new observer) re-tags and re-observes instead of trusting
    // a stale flag left on a re-rendered element.
    const gen = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    let raf = 0;
    const scan = () => {
      raf = 0;
      tagPage(document, io, gen);
      // Anything already on screen at scan time is revealed immediately, so a
      // section can never be left hidden waiting for a scroll that the visitor
      // has no reason to make.
      revealVisible(document);
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(scan); };

    // Initial scan after React has painted the new route.
    const t = setTimeout(scan, 30);
    // A second pass catches late layout shifts (fonts, images settling) that
    // move a section into view after the first scan measured it.
    const t2 = setTimeout(scan, 400);

    // Safety net: reveal on scroll/resize regardless of the observer. Cheap —
    // it only touches sections still missing .is-in, and there are none once
    // the page has been read through.
    const onView = () => revealVisible(document);
    window.addEventListener('scroll', onView, { passive: true });
    window.addEventListener('resize', onView, { passive: true });
    // Restoring a bfcache page (back/forward) does not re-run effects, but it
    // does fire pageshow — re-check rather than leaving content faded out.
    window.addEventListener('pageshow', onView);

    // Async content (blog posts, API-driven sections) — re-scan on DOM growth.
    const mo = new MutationObserver((muts) => {
      if (muts.some((m) => m.addedNodes.length)) schedule();
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(t);
      clearTimeout(t2);
      clearTimeout(enterTimer);
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onView);
      window.removeEventListener('resize', onView);
      window.removeEventListener('pageshow', onView);
      mo.disconnect();
      io.disconnect();
      document.body.classList.remove('ss-page-enter');
      // Leaving a public route must never strand faded-out content behind.
      // Anything still mid-reveal is shown outright.
      document.querySelectorAll('.ss-reveal:not(.is-in)').forEach((s) => s.classList.add('is-in'));
    };
  }, [pathname]);

  return null;
}
